<script lang="ts" setup>
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import AppTextarea from '@/components/atoms/textarea/AppTextarea.vue'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import AppDatePicker from '@/components/atoms/datepickers/AppDatePicker.vue'
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Calendar,
  Clock,
  User,
  Phone,
  Tag,
  AlertCircle,
  FileText,
  Trash,
  X,
  Trash2,
  Save,
} from 'lucide-vue-next'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

const { t } = useI18n()

interface Props {
  show: boolean
  type: 'create' | 'edit'
  dateInitial: string
  minTime: string
  maxTime: string
  editFormValue?: any
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:show': [v: boolean]
  save: [data: any]
  delete: [id: string]
}>()

const showDialog = computed({
  get: () => props.show,
  set: (v) => emit('update:show', v),
})

const durationOptions = [
  { name: '5 ' + t('calendar.minutes'), value: 5 },
  { name: '10 ' + t('calendar.minutes'), value: 10 },
  { name: '15 ' + t('calendar.minutes'), value: 15 },
  { name: '20 ' + t('calendar.minutes'), value: 20 },
  { name: '25 ' + t('calendar.minutes'), value: 25 },
  { name: '30 ' + t('calendar.minutes'), value: 30 },
]

const priorityOptions = [
  { name: t('calendar.priority.high'), value: '#dc2626', icon: AlertCircle },
  { name: t('calendar.priority.medium'), value: '#f97316', icon: AlertCircle },
  { name: t('calendar.priority.low'), value: '#22c55e', icon: AlertCircle },
]
const eventToDelete = ref<any>(null)

const timeToDate = (time: string) => {
  const [hours = '0', minutes = '0'] = time.split(':')
  const date = new Date()
  date.setHours(Number(hours), Number(minutes), 0, 0)
  return date
}

const dateToTime = (date: Date) => {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

const formDateToDate = (value: string) => {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const form = ref({
  idEvent: '',
  nameEvent: 'Llamada ',
  nameUser: '',
  phoneNumber: '',
  dateEvent: '',
  timeEvent: '07:00',
  eventDuration: 15,
  colorEvent: '#22c55e',
  descriptionEvent: '',
})

const timeEventDate = computed<Date | null>({
  get: () => timeToDate(form.value.timeEvent || props.minTime),
  set: (value) => {
    form.value.timeEvent = value ? dateToTime(value) : props.minTime
  },
})

const minTimeDate = computed(() => timeToDate(props.minTime))
const maxTimeDate = computed(() => timeToDate(props.maxTime))
const eventDateValue = computed(() => formDateToDate(form.value.dateEvent))

watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) return
    if (props.type === 'create') {
      form.value = {
        idEvent: '',
        nameEvent: 'Llamada ',
        nameUser: '',
        phoneNumber: '',
        dateEvent: props.dateInitial.split('T')[0],
        timeEvent: props.dateInitial.split('T')[1]?.slice(0, 5) || props.minTime,
        eventDuration: 15,
        colorEvent: '#22c55e',
        descriptionEvent: '',
      }
    } else {
      form.value = {
        ...props.editFormValue,
        dateEvent: props.dateInitial.split('T')[0],
        timeEvent: props.dateInitial.split('T')[1]?.slice(0, 5) || props.minTime,
      }
    }
  },
)

const save = () => {
  if (props.type === 'edit') {
    form.value.dateEvent = props.dateInitial.split('T')[0]
  }
  emit('save', form.value)
  showDialog.value = false
}

const confirmDeleteEvent = () => {
  eventToDelete.value = form.value
}

const deleteEvent = () => {
  if (!eventToDelete.value) return
  emit('delete', eventToDelete.value.idEvent)
  showDialog.value = false
  eventToDelete.value = null
}

const postponeCall = () => {
  form.value.colorEvent = '#334257'
  if (!form.value.nameEvent.includes('(pospuesta)')) form.value.nameEvent += ' (pospuesta)'
  save()
}

const makeCall = () => {
  form.value.colorEvent = '#8696fe'
  if (!form.value.nameEvent.includes('(realizada)')) form.value.nameEvent += ' (realizada)'
  save()
}
</script>

<template>
  <AppDialog
    v-model="showDialog"
    class="backdrop-blur-xl!"
    :style="{ width: '90vw', maxWidth: '500px' }"
  >
    <template #header>
      <label class="text-md font-medium">{{
        type === 'create' ? t('calendar.create_event') : t('calendar.edit_event')
      }}</label>
    </template>
    <template #content>
      <div class="p-1 overflow-y-auto">
        <div class="space-y-4">
          <div class="space-y-2">
            <label
              class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
            >
              <Tag class="w-4 h-4" />
              {{ t('calendar.event_title') }}
            </label>
            <AppInput
              v-model="form.nameEvent"
              required
              :placeholder="t('calendar.name_placeholder')"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2">
              <label
                class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
              >
                <User class="w-4 h-4" />
                {{ t('calendar.contact_name') }}
              </label>
              <AppInput v-model="form.nameUser" :placeholder="t('general.name')" required />
            </div>

            <div class="space-y-2">
              <label
                class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
              >
                <Phone class="w-4 h-4" />
                {{ t('calendar.phone_number') }}
              </label>
              <AppInput v-model="form.phoneNumber" :placeholder="t('general.eg_number')" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2" v-show="type !== 'edit'">
              <label
                class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
              >
                <Calendar class="w-4 h-4" />
                {{ t('calendar.event_date') }}
              </label>
              <AppDatePicker
                :model-value="eventDateValue"
                date-format="yy-mm-dd"
                disabled
                :show-clear="false"
                :manual-input="false"
                class="w-full"
              />
            </div>

            <div class="space-y-2">
              <!-- Hora del evento -->
              <label
                class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
              >
                <Clock class="w-4 h-4" />
                {{ t('calendar.event_time') }}
              </label>
              <AppDatePicker
                v-model="timeEventDate"
                time-only
                show-time
                hour-format="24"
                :step-minute="5"
                :min-date="minTimeDate"
                :max-date="maxTimeDate"
                :manual-input="false"
                required
                class="w-full"
              />
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ minTime }} - {{ maxTime }}</p>
            </div>

            <div class="space-y-2">
              <label
                class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
              >
                <Clock class="w-4 h-4" />
                {{ t('calendar.event_duration') }}
              </label>
              <AppSelect v-model="form.eventDuration" :options="durationOptions" :placeholder="t('calendar.select_duration')"/>
            </div>
          </div>

          <div class="space-y-3">
            <h3
              class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
            >
              <AlertCircle class="w-4 h-4" />
              {{ t('calendar.event_priority') }}
            </h3>
            <div class="flex gap-3">
              <button
                v-for="opt in priorityOptions"
                :key="opt.value"
                class="flex flex-col items-center gap-1 p-3 rounded-lg transition-all hover:scale-105"
                :style="{ backgroundColor: `${opt.value}15` }"
                :class="{
                  'ring-2  ring-[var(--p-primary-300)] dark:ring-[var(--p-primary-700)]/90': form.colorEvent === opt.value,
                  'border border-gray-200 dark:border-gray-700': form.colorEvent !== opt.value,
                }"
                @click="form.colorEvent = opt.value"
                :title="opt.name"
              >
                <div
                  class="w-6 h-6 rounded-full flex items-center justify-center"
                  :style="{ backgroundColor: opt.value }"
                >
                  <component :is="opt.icon" class="w-3 h-3 text-white" />
                </div>
                <span class="text-xs font-medium" :style="{ color: opt.value }">
                  {{ opt.name }}
                </span>
              </button>
            </div>
          </div>

          <div class="space-y-2">
            <label
              class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-[var(--p-surface-0)]"
            >
              <FileText class="w-4 h-4" />
              {{ t('calendar.event_description') }}
            </label>
            <AppTextarea
              v-model="form.descriptionEvent"
              :rows="4"
              :placeholder="t('calendar.event_description') + '...'"
              class="pt-3"
            />
          </div>

          <div v-if="type === 'edit'">
            <div class="flex flex-col sm:flex-row gap-2">
              <AppButton variant="success" :label="t('calendar.make_call')" @click="makeCall" />
              <AppButton
                variant="warn"
                :label="t('calendar.postpone_call')"
                @click="postponeCall"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col sm:flex-row justify-end gap-3 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700"
      >
        <AppButtonIcon
          severity="secondary"
          :label="t('general.cancel')"
          @click="showDialog = false"
        >
          <template #icon>
            <X :size="16" />
          </template>
        </AppButtonIcon>

        <AppButtonIcon
          v-if="type === 'edit'"
          severity="danger"
          :label="t('general.delete')"
          @click="confirmDeleteEvent"
        >
          <template #icon>
            <Trash2 :size="16" />
          </template>
        </AppButtonIcon>

        <AppButtonIcon severity="primary" :label="t('general.save')" @click="save">
          <template #icon>
            <Save :size="16" />
          </template>
        </AppButtonIcon>
      </div>
      <!-- Modal de confirmación de eliminación -->
      <div
        v-if="eventToDelete"
        class="fixed inset-0 z-[20000] bg-black/40 flex items-center justify-center"
        @click.self="eventToDelete = null"
      >
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-11/12 max-w-sm border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-3">
            <Trash :size="25" />
            <span class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ t('calendar.delete_title') }}
            </span>
          </div>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {{ t('calendar.delete_message') }} “{{ eventToDelete.nameEvent }}”?
          </p>
          <div class="mt-6 flex justify-end gap-2">
            <AppButton
              variant="secondary"
              :label="t('general.cancel')"
              @click="eventToDelete = null"
            />
            <AppButton variant="danger" :label="t('general.delete')" @click="deleteEvent" />
          </div>
        </div>
      </div>
    </template>
  </AppDialog>
</template>
